/**
 * What the cauldron gives back, and what it takes (M4-07).
 *
 * In the Second Branch of the Mabinogi, the warriors thrown into the Pair
 * Dadeni come out whole and fighting and **unable to speak**. That is the whole
 * design of death in Annwn, generalised: nobody is deleted, and nobody comes
 * back the same. Each rebirth draws a mark, the marks stack, and a character's
 * deaths become part of their biography exactly as their scars are (AoD §12.3).
 *
 * Three commitments:
 *
 * 1. **Marks are content.** This module knows what a mark *can* do — silence a
 *    voice, thin a face, sharpen a hunger — and content says which marks exist
 *    and what each is called. A mark is data, like a herb and a wound table
 *    cell, so a balance pass never edits a rule.
 * 2. **A mark is never a stat.** Nothing here grants dice, health or damage.
 *    Every effect modifies a process the body already had, in the same way the
 *    pharmacopoeia does — which is what keeps a run of bad luck from becoming a
 *    death spiral of numbers.
 * 3. **They live on the body.** `Vitals.marks` rides with the character record
 *    (M3-07) and so survives a restart without a new table, a new persistence
 *    field or a new place for the truth to live. A body that came back from the
 *    cauldron is a fact about that body.
 */
/* -------------------------------------------------------------------------- *
 * Reading a body's marks
 * -------------------------------------------------------------------------- */
/** The marks on this body, resolved against content. Unknown ids are skipped. */
export function marksOf(source, vitals) {
    const out = [];
    for (const id of vitals.marks ?? []) {
        const def = source.mark?.(id);
        if (def)
            out.push(def);
    }
    return out;
}
export function bears(vitals, id) {
    return (vitals.marks ?? []).includes(id);
}
/**
 * Is this character unable to speak (M4-07)?
 *
 * Asked by `speech.ts` before a word leaves anybody's mouth. Resolved through
 * content rather than by comparing against the string `'mute'`, because which
 * mark silences you is content's decision and there may one day be two.
 */
export function silenced(source, vitals) {
    return marksOf(source, vitals).some((mark) => mark.mute === true);
}
/** Does this body draw the eye as one that has been through the cauldron? */
export function palled(source, vitals) {
    return marksOf(source, vitals).some((mark) => mark.pale === true);
}
/** Does the night bring them the hounds? */
export function houndDreamed(source, vitals) {
    return marksOf(source, vitals).some((mark) => mark.hounds === true);
}
/**
 * How fast hunger bites, as a multiplier (M4-07's Cold-Hungered).
 *
 * Multiplied rather than summed across marks, so two hungers would compound
 * the way two coats of the same cold would — and 1 for a body with none, so
 * nothing pays for the machinery until somebody has died.
 */
export function hungerRate(source, vitals) {
    let rate = 1;
    for (const mark of marksOf(source, vitals))
        rate *= mark.hungerRate ?? 1;
    return rate;
}
/**
 * Pain the weather is worth to an old body (M4-07's Old-Ache).
 *
 * Summed, and only while it is actually raining. Charged as *pain* rather than
 * as a pool penalty so that grit reduces it and willow bark quiets it, exactly
 * like every other ache in the game — a mark that bypassed the medicine system
 * would be a mark you could do nothing about.
 */
export function achePain(source, vitals, raining) {
    if (!raining)
        return 0;
    let pain = 0;
    for (const mark of marksOf(source, vitals))
        pain += mark.rainPain ?? 0;
    return pain;
}
/* -------------------------------------------------------------------------- *
 * Taking one
 * -------------------------------------------------------------------------- */
/**
 * Draw the mark this death leaves, or `null` when there is nothing left to take.
 *
 * Drawn from the marks this character does **not** already bear, so a second
 * death is a second kind of loss rather than the same one twice — which is what
 * makes a stack of them read as a biography. Seeded, like every other roll in
 * the game, so a replay of a death produces the same returned man.
 *
 * `null` once somebody has collected the lot. There is no rebirth cap in the
 * MVP (the index README's open question), so the honest answer to "what does
 * the ninth death cost" is: nothing further, and eight testers will tell us
 * whether that is a problem.
 */
export function drawMark(source, rng, vitals) {
    const borne = new Set(vitals.marks ?? []);
    const available = (source.marks?.() ?? []).filter((mark) => !borne.has(mark.id));
    if (available.length === 0)
        return null;
    return available[rng.int(0, available.length)] ?? null;
}
/** Write a mark onto a body. Idempotent and sorted, like `learn` in `item.ts`. */
export function bearMark(vitals, id) {
    const marks = (vitals.marks ??= []);
    if (marks.includes(id))
        return false;
    marks.push(id);
    marks.sort();
    return true;
}
//# sourceMappingURL=marks.js.map